// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

// Code generated by Microsoft (R) AutoRest Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is regenerated.

import type { PagedAsyncIterableIterator } from "@azure/core-paging";
import type { SimplePollerLike, OperationState } from "@azure/core-lro";
import type {
  SqlDatabaseGetResults,
  SqlResourcesListSqlDatabasesOptionalParams,
  SqlContainerGetResults,
  SqlResourcesListSqlContainersOptionalParams,
  ClientEncryptionKeyGetResults,
  SqlResourcesListClientEncryptionKeysOptionalParams,
  SqlStoredProcedureGetResults,
  SqlResourcesListSqlStoredProceduresOptionalParams,
  SqlUserDefinedFunctionGetResults,
  SqlResourcesListSqlUserDefinedFunctionsOptionalParams,
  SqlTriggerGetResults,
  SqlResourcesListSqlTriggersOptionalParams,
  SqlRoleDefinitionGetResults,
  SqlResourcesListSqlRoleDefinitionsOptionalParams,
  SqlRoleAssignmentGetResults,
  SqlResourcesListSqlRoleAssignmentsOptionalParams,
  SqlResourcesGetSqlDatabaseOptionalParams,
  SqlResourcesGetSqlDatabaseResponse,
  SqlDatabaseCreateUpdateParameters,
  SqlResourcesCreateUpdateSqlDatabaseOptionalParams,
  SqlResourcesCreateUpdateSqlDatabaseResponse,
  SqlResourcesDeleteSqlDatabaseOptionalParams,
  SqlResourcesDeleteSqlDatabaseResponse,
  SqlResourcesGetSqlDatabaseThroughputOptionalParams,
  SqlResourcesGetSqlDatabaseThroughputResponse,
  ThroughputSettingsUpdateParameters,
  SqlResourcesUpdateSqlDatabaseThroughputOptionalParams,
  SqlResourcesUpdateSqlDatabaseThroughputResponse,
  SqlResourcesMigrateSqlDatabaseToAutoscaleOptionalParams,
  SqlResourcesMigrateSqlDatabaseToAutoscaleResponse,
  SqlResourcesMigrateSqlDatabaseToManualThroughputOptionalParams,
  SqlResourcesMigrateSqlDatabaseToManualThroughputResponse,
  SqlResourcesGetSqlContainerOptionalParams,
  SqlResourcesGetSqlContainerResponse,
  SqlContainerCreateUpdateParameters,
  SqlResourcesCreateUpdateSqlContainerOptionalParams,
  SqlResourcesCreateUpdateSqlContainerResponse,
  SqlResourcesDeleteSqlContainerOptionalParams,
  SqlResourcesDeleteSqlContainerResponse,
  SqlResourcesGetSqlContainerThroughputOptionalParams,
  SqlResourcesGetSqlContainerThroughputResponse,
  SqlResourcesUpdateSqlContainerThroughputOptionalParams,
  SqlResourcesUpdateSqlContainerThroughputResponse,
  SqlResourcesMigrateSqlContainerToAutoscaleOptionalParams,
  SqlResourcesMigrateSqlContainerToAutoscaleResponse,
  SqlResourcesMigrateSqlContainerToManualThroughputOptionalParams,
  SqlResourcesMigrateSqlContainerToManualThroughputResponse,
  SqlResourcesGetClientEncryptionKeyOptionalParams,
  SqlResourcesGetClientEncryptionKeyResponse,
  ClientEncryptionKeyCreateUpdateParameters,
  SqlResourcesCreateUpdateClientEncryptionKeyOptionalParams,
  SqlResourcesCreateUpdateClientEncryptionKeyResponse,
  SqlResourcesGetSqlStoredProcedureOptionalParams,
  SqlResourcesGetSqlStoredProcedureResponse,
  SqlStoredProcedureCreateUpdateParameters,
  SqlResourcesCreateUpdateSqlStoredProcedureOptionalParams,
  SqlResourcesCreateUpdateSqlStoredProcedureResponse,
  SqlResourcesDeleteSqlStoredProcedureOptionalParams,
  SqlResourcesDeleteSqlStoredProcedureResponse,
  SqlResourcesGetSqlUserDefinedFunctionOptionalParams,
  SqlResourcesGetSqlUserDefinedFunctionResponse,
  SqlUserDefinedFunctionCreateUpdateParameters,
  SqlResourcesCreateUpdateSqlUserDefinedFunctionOptionalParams,
  SqlResourcesCreateUpdateSqlUserDefinedFunctionResponse,
  SqlResourcesDeleteSqlUserDefinedFunctionOptionalParams,
  SqlResourcesDeleteSqlUserDefinedFunctionResponse,
  SqlResourcesGetSqlTriggerOptionalParams,
  SqlResourcesGetSqlTriggerResponse,
  SqlTriggerCreateUpdateParameters,
  SqlResourcesCreateUpdateSqlTriggerOptionalParams,
  SqlResourcesCreateUpdateSqlTriggerResponse,
  SqlResourcesDeleteSqlTriggerOptionalParams,
  SqlResourcesDeleteSqlTriggerResponse,
  SqlResourcesGetSqlRoleDefinitionOptionalParams,
  SqlResourcesGetSqlRoleDefinitionResponse,
  SqlRoleDefinitionCreateUpdateParameters,
  SqlResourcesCreateUpdateSqlRoleDefinitionOptionalParams,
  SqlResourcesCreateUpdateSqlRoleDefinitionResponse,
  SqlResourcesDeleteSqlRoleDefinitionOptionalParams,
  SqlResourcesGetSqlRoleAssignmentOptionalParams,
  SqlResourcesGetSqlRoleAssignmentResponse,
  SqlRoleAssignmentCreateUpdateParameters,
  SqlResourcesCreateUpdateSqlRoleAssignmentOptionalParams,
  SqlResourcesCreateUpdateSqlRoleAssignmentResponse,
  SqlResourcesDeleteSqlRoleAssignmentOptionalParams,
  ContinuousBackupRestoreLocation,
  SqlResourcesRetrieveContinuousBackupInformationOptionalParams,
  SqlResourcesRetrieveContinuousBackupInformationResponse,
} from "../models/index.js";

/// <reference lib="esnext.asynciterable" />
/** Interface representing a SqlResources. */
export interface SqlResources {
  /**
   * Lists the SQL databases under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  listSqlDatabases(
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesListSqlDatabasesOptionalParams,
  ): PagedAsyncIterableIterator<SqlDatabaseGetResults>;
  /**
   * Lists the SQL container under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  listSqlContainers(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesListSqlContainersOptionalParams,
  ): PagedAsyncIterableIterator<SqlContainerGetResults>;
  /**
   * Lists the ClientEncryptionKeys under an existing Azure Cosmos DB SQL database.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  listClientEncryptionKeys(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesListClientEncryptionKeysOptionalParams,
  ): PagedAsyncIterableIterator<ClientEncryptionKeyGetResults>;
  /**
   * Lists the SQL storedProcedure under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  listSqlStoredProcedures(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesListSqlStoredProceduresOptionalParams,
  ): PagedAsyncIterableIterator<SqlStoredProcedureGetResults>;
  /**
   * Lists the SQL userDefinedFunction under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  listSqlUserDefinedFunctions(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesListSqlUserDefinedFunctionsOptionalParams,
  ): PagedAsyncIterableIterator<SqlUserDefinedFunctionGetResults>;
  /**
   * Lists the SQL trigger under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  listSqlTriggers(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesListSqlTriggersOptionalParams,
  ): PagedAsyncIterableIterator<SqlTriggerGetResults>;
  /**
   * Retrieves the list of all Azure Cosmos DB SQL Role Definitions.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  listSqlRoleDefinitions(
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesListSqlRoleDefinitionsOptionalParams,
  ): PagedAsyncIterableIterator<SqlRoleDefinitionGetResults>;
  /**
   * Retrieves the list of all Azure Cosmos DB SQL Role Assignments.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  listSqlRoleAssignments(
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesListSqlRoleAssignmentsOptionalParams,
  ): PagedAsyncIterableIterator<SqlRoleAssignmentGetResults>;
  /**
   * Gets the SQL database under an existing Azure Cosmos DB database account with the provided name.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  getSqlDatabase(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesGetSqlDatabaseOptionalParams,
  ): Promise<SqlResourcesGetSqlDatabaseResponse>;
  /**
   * Create or update an Azure Cosmos DB SQL database
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param createUpdateSqlDatabaseParameters The parameters to provide for the current SQL database.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlDatabase(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    createUpdateSqlDatabaseParameters: SqlDatabaseCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlDatabaseOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateSqlDatabaseResponse>,
      SqlResourcesCreateUpdateSqlDatabaseResponse
    >
  >;
  /**
   * Create or update an Azure Cosmos DB SQL database
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param createUpdateSqlDatabaseParameters The parameters to provide for the current SQL database.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlDatabaseAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    createUpdateSqlDatabaseParameters: SqlDatabaseCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlDatabaseOptionalParams,
  ): Promise<SqlResourcesCreateUpdateSqlDatabaseResponse>;
  /**
   * Deletes an existing Azure Cosmos DB SQL database.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  beginDeleteSqlDatabase(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesDeleteSqlDatabaseOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesDeleteSqlDatabaseResponse>,
      SqlResourcesDeleteSqlDatabaseResponse
    >
  >;
  /**
   * Deletes an existing Azure Cosmos DB SQL database.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  beginDeleteSqlDatabaseAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesDeleteSqlDatabaseOptionalParams,
  ): Promise<SqlResourcesDeleteSqlDatabaseResponse>;
  /**
   * Gets the RUs per second of the SQL database under an existing Azure Cosmos DB database account with
   * the provided name.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  getSqlDatabaseThroughput(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesGetSqlDatabaseThroughputOptionalParams,
  ): Promise<SqlResourcesGetSqlDatabaseThroughputResponse>;
  /**
   * Update RUs per second of an Azure Cosmos DB SQL database
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param updateThroughputParameters The parameters to provide for the RUs per second of the current
   *                                   SQL database.
   * @param options The options parameters.
   */
  beginUpdateSqlDatabaseThroughput(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    updateThroughputParameters: ThroughputSettingsUpdateParameters,
    options?: SqlResourcesUpdateSqlDatabaseThroughputOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesUpdateSqlDatabaseThroughputResponse>,
      SqlResourcesUpdateSqlDatabaseThroughputResponse
    >
  >;
  /**
   * Update RUs per second of an Azure Cosmos DB SQL database
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param updateThroughputParameters The parameters to provide for the RUs per second of the current
   *                                   SQL database.
   * @param options The options parameters.
   */
  beginUpdateSqlDatabaseThroughputAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    updateThroughputParameters: ThroughputSettingsUpdateParameters,
    options?: SqlResourcesUpdateSqlDatabaseThroughputOptionalParams,
  ): Promise<SqlResourcesUpdateSqlDatabaseThroughputResponse>;
  /**
   * Migrate an Azure Cosmos DB SQL database from manual throughput to autoscale
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  beginMigrateSqlDatabaseToAutoscale(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesMigrateSqlDatabaseToAutoscaleOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesMigrateSqlDatabaseToAutoscaleResponse>,
      SqlResourcesMigrateSqlDatabaseToAutoscaleResponse
    >
  >;
  /**
   * Migrate an Azure Cosmos DB SQL database from manual throughput to autoscale
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  beginMigrateSqlDatabaseToAutoscaleAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesMigrateSqlDatabaseToAutoscaleOptionalParams,
  ): Promise<SqlResourcesMigrateSqlDatabaseToAutoscaleResponse>;
  /**
   * Migrate an Azure Cosmos DB SQL database from autoscale to manual throughput
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  beginMigrateSqlDatabaseToManualThroughput(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesMigrateSqlDatabaseToManualThroughputOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesMigrateSqlDatabaseToManualThroughputResponse>,
      SqlResourcesMigrateSqlDatabaseToManualThroughputResponse
    >
  >;
  /**
   * Migrate an Azure Cosmos DB SQL database from autoscale to manual throughput
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param options The options parameters.
   */
  beginMigrateSqlDatabaseToManualThroughputAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    options?: SqlResourcesMigrateSqlDatabaseToManualThroughputOptionalParams,
  ): Promise<SqlResourcesMigrateSqlDatabaseToManualThroughputResponse>;
  /**
   * Gets the SQL container under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  getSqlContainer(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesGetSqlContainerOptionalParams,
  ): Promise<SqlResourcesGetSqlContainerResponse>;
  /**
   * Create or update an Azure Cosmos DB SQL container
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param createUpdateSqlContainerParameters The parameters to provide for the current SQL container.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlContainer(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    createUpdateSqlContainerParameters: SqlContainerCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlContainerOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateSqlContainerResponse>,
      SqlResourcesCreateUpdateSqlContainerResponse
    >
  >;
  /**
   * Create or update an Azure Cosmos DB SQL container
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param createUpdateSqlContainerParameters The parameters to provide for the current SQL container.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlContainerAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    createUpdateSqlContainerParameters: SqlContainerCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlContainerOptionalParams,
  ): Promise<SqlResourcesCreateUpdateSqlContainerResponse>;
  /**
   * Deletes an existing Azure Cosmos DB SQL container.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  beginDeleteSqlContainer(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesDeleteSqlContainerOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesDeleteSqlContainerResponse>,
      SqlResourcesDeleteSqlContainerResponse
    >
  >;
  /**
   * Deletes an existing Azure Cosmos DB SQL container.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  beginDeleteSqlContainerAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesDeleteSqlContainerOptionalParams,
  ): Promise<SqlResourcesDeleteSqlContainerResponse>;
  /**
   * Gets the RUs per second of the SQL container under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  getSqlContainerThroughput(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesGetSqlContainerThroughputOptionalParams,
  ): Promise<SqlResourcesGetSqlContainerThroughputResponse>;
  /**
   * Update RUs per second of an Azure Cosmos DB SQL container
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param updateThroughputParameters The parameters to provide for the RUs per second of the current
   *                                   SQL container.
   * @param options The options parameters.
   */
  beginUpdateSqlContainerThroughput(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    updateThroughputParameters: ThroughputSettingsUpdateParameters,
    options?: SqlResourcesUpdateSqlContainerThroughputOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesUpdateSqlContainerThroughputResponse>,
      SqlResourcesUpdateSqlContainerThroughputResponse
    >
  >;
  /**
   * Update RUs per second of an Azure Cosmos DB SQL container
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param updateThroughputParameters The parameters to provide for the RUs per second of the current
   *                                   SQL container.
   * @param options The options parameters.
   */
  beginUpdateSqlContainerThroughputAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    updateThroughputParameters: ThroughputSettingsUpdateParameters,
    options?: SqlResourcesUpdateSqlContainerThroughputOptionalParams,
  ): Promise<SqlResourcesUpdateSqlContainerThroughputResponse>;
  /**
   * Migrate an Azure Cosmos DB SQL container from manual throughput to autoscale
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  beginMigrateSqlContainerToAutoscale(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesMigrateSqlContainerToAutoscaleOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesMigrateSqlContainerToAutoscaleResponse>,
      SqlResourcesMigrateSqlContainerToAutoscaleResponse
    >
  >;
  /**
   * Migrate an Azure Cosmos DB SQL container from manual throughput to autoscale
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  beginMigrateSqlContainerToAutoscaleAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesMigrateSqlContainerToAutoscaleOptionalParams,
  ): Promise<SqlResourcesMigrateSqlContainerToAutoscaleResponse>;
  /**
   * Migrate an Azure Cosmos DB SQL container from autoscale to manual throughput
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  beginMigrateSqlContainerToManualThroughput(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesMigrateSqlContainerToManualThroughputOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesMigrateSqlContainerToManualThroughputResponse>,
      SqlResourcesMigrateSqlContainerToManualThroughputResponse
    >
  >;
  /**
   * Migrate an Azure Cosmos DB SQL container from autoscale to manual throughput
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param options The options parameters.
   */
  beginMigrateSqlContainerToManualThroughputAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    options?: SqlResourcesMigrateSqlContainerToManualThroughputOptionalParams,
  ): Promise<SqlResourcesMigrateSqlContainerToManualThroughputResponse>;
  /**
   * Gets the ClientEncryptionKey under an existing Azure Cosmos DB SQL database.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param clientEncryptionKeyName Cosmos DB ClientEncryptionKey name.
   * @param options The options parameters.
   */
  getClientEncryptionKey(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    clientEncryptionKeyName: string,
    options?: SqlResourcesGetClientEncryptionKeyOptionalParams,
  ): Promise<SqlResourcesGetClientEncryptionKeyResponse>;
  /**
   * Create or update a ClientEncryptionKey. This API is meant to be invoked via tools such as the Azure
   * Powershell (instead of directly).
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param clientEncryptionKeyName Cosmos DB ClientEncryptionKey name.
   * @param createUpdateClientEncryptionKeyParameters The parameters to provide for the client encryption
   *                                                  key.
   * @param options The options parameters.
   */
  beginCreateUpdateClientEncryptionKey(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    clientEncryptionKeyName: string,
    createUpdateClientEncryptionKeyParameters: ClientEncryptionKeyCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateClientEncryptionKeyOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateClientEncryptionKeyResponse>,
      SqlResourcesCreateUpdateClientEncryptionKeyResponse
    >
  >;
  /**
   * Create or update a ClientEncryptionKey. This API is meant to be invoked via tools such as the Azure
   * Powershell (instead of directly).
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param clientEncryptionKeyName Cosmos DB ClientEncryptionKey name.
   * @param createUpdateClientEncryptionKeyParameters The parameters to provide for the client encryption
   *                                                  key.
   * @param options The options parameters.
   */
  beginCreateUpdateClientEncryptionKeyAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    clientEncryptionKeyName: string,
    createUpdateClientEncryptionKeyParameters: ClientEncryptionKeyCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateClientEncryptionKeyOptionalParams,
  ): Promise<SqlResourcesCreateUpdateClientEncryptionKeyResponse>;
  /**
   * Gets the SQL storedProcedure under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param storedProcedureName Cosmos DB storedProcedure name.
   * @param options The options parameters.
   */
  getSqlStoredProcedure(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    storedProcedureName: string,
    options?: SqlResourcesGetSqlStoredProcedureOptionalParams,
  ): Promise<SqlResourcesGetSqlStoredProcedureResponse>;
  /**
   * Create or update an Azure Cosmos DB SQL storedProcedure
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param storedProcedureName Cosmos DB storedProcedure name.
   * @param createUpdateSqlStoredProcedureParameters The parameters to provide for the current SQL
   *                                                 storedProcedure.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlStoredProcedure(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    storedProcedureName: string,
    createUpdateSqlStoredProcedureParameters: SqlStoredProcedureCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlStoredProcedureOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateSqlStoredProcedureResponse>,
      SqlResourcesCreateUpdateSqlStoredProcedureResponse
    >
  >;
  /**
   * Create or update an Azure Cosmos DB SQL storedProcedure
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param storedProcedureName Cosmos DB storedProcedure name.
   * @param createUpdateSqlStoredProcedureParameters The parameters to provide for the current SQL
   *                                                 storedProcedure.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlStoredProcedureAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    storedProcedureName: string,
    createUpdateSqlStoredProcedureParameters: SqlStoredProcedureCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlStoredProcedureOptionalParams,
  ): Promise<SqlResourcesCreateUpdateSqlStoredProcedureResponse>;
  /**
   * Deletes an existing Azure Cosmos DB SQL storedProcedure.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param storedProcedureName Cosmos DB storedProcedure name.
   * @param options The options parameters.
   */
  beginDeleteSqlStoredProcedure(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    storedProcedureName: string,
    options?: SqlResourcesDeleteSqlStoredProcedureOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesDeleteSqlStoredProcedureResponse>,
      SqlResourcesDeleteSqlStoredProcedureResponse
    >
  >;
  /**
   * Deletes an existing Azure Cosmos DB SQL storedProcedure.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param storedProcedureName Cosmos DB storedProcedure name.
   * @param options The options parameters.
   */
  beginDeleteSqlStoredProcedureAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    storedProcedureName: string,
    options?: SqlResourcesDeleteSqlStoredProcedureOptionalParams,
  ): Promise<SqlResourcesDeleteSqlStoredProcedureResponse>;
  /**
   * Gets the SQL userDefinedFunction under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param userDefinedFunctionName Cosmos DB userDefinedFunction name.
   * @param options The options parameters.
   */
  getSqlUserDefinedFunction(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    userDefinedFunctionName: string,
    options?: SqlResourcesGetSqlUserDefinedFunctionOptionalParams,
  ): Promise<SqlResourcesGetSqlUserDefinedFunctionResponse>;
  /**
   * Create or update an Azure Cosmos DB SQL userDefinedFunction
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param userDefinedFunctionName Cosmos DB userDefinedFunction name.
   * @param createUpdateSqlUserDefinedFunctionParameters The parameters to provide for the current SQL
   *                                                     userDefinedFunction.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlUserDefinedFunction(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    userDefinedFunctionName: string,
    createUpdateSqlUserDefinedFunctionParameters: SqlUserDefinedFunctionCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlUserDefinedFunctionOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateSqlUserDefinedFunctionResponse>,
      SqlResourcesCreateUpdateSqlUserDefinedFunctionResponse
    >
  >;
  /**
   * Create or update an Azure Cosmos DB SQL userDefinedFunction
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param userDefinedFunctionName Cosmos DB userDefinedFunction name.
   * @param createUpdateSqlUserDefinedFunctionParameters The parameters to provide for the current SQL
   *                                                     userDefinedFunction.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlUserDefinedFunctionAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    userDefinedFunctionName: string,
    createUpdateSqlUserDefinedFunctionParameters: SqlUserDefinedFunctionCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlUserDefinedFunctionOptionalParams,
  ): Promise<SqlResourcesCreateUpdateSqlUserDefinedFunctionResponse>;
  /**
   * Deletes an existing Azure Cosmos DB SQL userDefinedFunction.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param userDefinedFunctionName Cosmos DB userDefinedFunction name.
   * @param options The options parameters.
   */
  beginDeleteSqlUserDefinedFunction(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    userDefinedFunctionName: string,
    options?: SqlResourcesDeleteSqlUserDefinedFunctionOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesDeleteSqlUserDefinedFunctionResponse>,
      SqlResourcesDeleteSqlUserDefinedFunctionResponse
    >
  >;
  /**
   * Deletes an existing Azure Cosmos DB SQL userDefinedFunction.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param userDefinedFunctionName Cosmos DB userDefinedFunction name.
   * @param options The options parameters.
   */
  beginDeleteSqlUserDefinedFunctionAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    userDefinedFunctionName: string,
    options?: SqlResourcesDeleteSqlUserDefinedFunctionOptionalParams,
  ): Promise<SqlResourcesDeleteSqlUserDefinedFunctionResponse>;
  /**
   * Gets the SQL trigger under an existing Azure Cosmos DB database account.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param triggerName Cosmos DB trigger name.
   * @param options The options parameters.
   */
  getSqlTrigger(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    triggerName: string,
    options?: SqlResourcesGetSqlTriggerOptionalParams,
  ): Promise<SqlResourcesGetSqlTriggerResponse>;
  /**
   * Create or update an Azure Cosmos DB SQL trigger
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param triggerName Cosmos DB trigger name.
   * @param createUpdateSqlTriggerParameters The parameters to provide for the current SQL trigger.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlTrigger(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    triggerName: string,
    createUpdateSqlTriggerParameters: SqlTriggerCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlTriggerOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateSqlTriggerResponse>,
      SqlResourcesCreateUpdateSqlTriggerResponse
    >
  >;
  /**
   * Create or update an Azure Cosmos DB SQL trigger
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param triggerName Cosmos DB trigger name.
   * @param createUpdateSqlTriggerParameters The parameters to provide for the current SQL trigger.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlTriggerAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    triggerName: string,
    createUpdateSqlTriggerParameters: SqlTriggerCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlTriggerOptionalParams,
  ): Promise<SqlResourcesCreateUpdateSqlTriggerResponse>;
  /**
   * Deletes an existing Azure Cosmos DB SQL trigger.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param triggerName Cosmos DB trigger name.
   * @param options The options parameters.
   */
  beginDeleteSqlTrigger(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    triggerName: string,
    options?: SqlResourcesDeleteSqlTriggerOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesDeleteSqlTriggerResponse>,
      SqlResourcesDeleteSqlTriggerResponse
    >
  >;
  /**
   * Deletes an existing Azure Cosmos DB SQL trigger.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param triggerName Cosmos DB trigger name.
   * @param options The options parameters.
   */
  beginDeleteSqlTriggerAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    triggerName: string,
    options?: SqlResourcesDeleteSqlTriggerOptionalParams,
  ): Promise<SqlResourcesDeleteSqlTriggerResponse>;
  /**
   * Retrieves the properties of an existing Azure Cosmos DB SQL Role Definition with the given Id.
   * @param roleDefinitionId The GUID for the Role Definition.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  getSqlRoleDefinition(
    roleDefinitionId: string,
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesGetSqlRoleDefinitionOptionalParams,
  ): Promise<SqlResourcesGetSqlRoleDefinitionResponse>;
  /**
   * Creates or updates an Azure Cosmos DB SQL Role Definition.
   * @param roleDefinitionId The GUID for the Role Definition.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param createUpdateSqlRoleDefinitionParameters The properties required to create or update a Role
   *                                                Definition.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlRoleDefinition(
    roleDefinitionId: string,
    resourceGroupName: string,
    accountName: string,
    createUpdateSqlRoleDefinitionParameters: SqlRoleDefinitionCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlRoleDefinitionOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateSqlRoleDefinitionResponse>,
      SqlResourcesCreateUpdateSqlRoleDefinitionResponse
    >
  >;
  /**
   * Creates or updates an Azure Cosmos DB SQL Role Definition.
   * @param roleDefinitionId The GUID for the Role Definition.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param createUpdateSqlRoleDefinitionParameters The properties required to create or update a Role
   *                                                Definition.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlRoleDefinitionAndWait(
    roleDefinitionId: string,
    resourceGroupName: string,
    accountName: string,
    createUpdateSqlRoleDefinitionParameters: SqlRoleDefinitionCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlRoleDefinitionOptionalParams,
  ): Promise<SqlResourcesCreateUpdateSqlRoleDefinitionResponse>;
  /**
   * Deletes an existing Azure Cosmos DB SQL Role Definition.
   * @param roleDefinitionId The GUID for the Role Definition.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  beginDeleteSqlRoleDefinition(
    roleDefinitionId: string,
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesDeleteSqlRoleDefinitionOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes an existing Azure Cosmos DB SQL Role Definition.
   * @param roleDefinitionId The GUID for the Role Definition.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  beginDeleteSqlRoleDefinitionAndWait(
    roleDefinitionId: string,
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesDeleteSqlRoleDefinitionOptionalParams,
  ): Promise<void>;
  /**
   * Retrieves the properties of an existing Azure Cosmos DB SQL Role Assignment with the given Id.
   * @param roleAssignmentId The GUID for the Role Assignment.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  getSqlRoleAssignment(
    roleAssignmentId: string,
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesGetSqlRoleAssignmentOptionalParams,
  ): Promise<SqlResourcesGetSqlRoleAssignmentResponse>;
  /**
   * Creates or updates an Azure Cosmos DB SQL Role Assignment.
   * @param roleAssignmentId The GUID for the Role Assignment.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param createUpdateSqlRoleAssignmentParameters The properties required to create or update a Role
   *                                                Assignment.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlRoleAssignment(
    roleAssignmentId: string,
    resourceGroupName: string,
    accountName: string,
    createUpdateSqlRoleAssignmentParameters: SqlRoleAssignmentCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlRoleAssignmentOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesCreateUpdateSqlRoleAssignmentResponse>,
      SqlResourcesCreateUpdateSqlRoleAssignmentResponse
    >
  >;
  /**
   * Creates or updates an Azure Cosmos DB SQL Role Assignment.
   * @param roleAssignmentId The GUID for the Role Assignment.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param createUpdateSqlRoleAssignmentParameters The properties required to create or update a Role
   *                                                Assignment.
   * @param options The options parameters.
   */
  beginCreateUpdateSqlRoleAssignmentAndWait(
    roleAssignmentId: string,
    resourceGroupName: string,
    accountName: string,
    createUpdateSqlRoleAssignmentParameters: SqlRoleAssignmentCreateUpdateParameters,
    options?: SqlResourcesCreateUpdateSqlRoleAssignmentOptionalParams,
  ): Promise<SqlResourcesCreateUpdateSqlRoleAssignmentResponse>;
  /**
   * Deletes an existing Azure Cosmos DB SQL Role Assignment.
   * @param roleAssignmentId The GUID for the Role Assignment.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  beginDeleteSqlRoleAssignment(
    roleAssignmentId: string,
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesDeleteSqlRoleAssignmentOptionalParams,
  ): Promise<SimplePollerLike<OperationState<void>, void>>;
  /**
   * Deletes an existing Azure Cosmos DB SQL Role Assignment.
   * @param roleAssignmentId The GUID for the Role Assignment.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param options The options parameters.
   */
  beginDeleteSqlRoleAssignmentAndWait(
    roleAssignmentId: string,
    resourceGroupName: string,
    accountName: string,
    options?: SqlResourcesDeleteSqlRoleAssignmentOptionalParams,
  ): Promise<void>;
  /**
   * Retrieves continuous backup information for a container resource.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param location The name of the continuous backup restore location.
   * @param options The options parameters.
   */
  beginRetrieveContinuousBackupInformation(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    location: ContinuousBackupRestoreLocation,
    options?: SqlResourcesRetrieveContinuousBackupInformationOptionalParams,
  ): Promise<
    SimplePollerLike<
      OperationState<SqlResourcesRetrieveContinuousBackupInformationResponse>,
      SqlResourcesRetrieveContinuousBackupInformationResponse
    >
  >;
  /**
   * Retrieves continuous backup information for a container resource.
   * @param resourceGroupName The name of the resource group. The name is case insensitive.
   * @param accountName Cosmos DB database account name.
   * @param databaseName Cosmos DB database name.
   * @param containerName Cosmos DB container name.
   * @param location The name of the continuous backup restore location.
   * @param options The options parameters.
   */
  beginRetrieveContinuousBackupInformationAndWait(
    resourceGroupName: string,
    accountName: string,
    databaseName: string,
    containerName: string,
    location: ContinuousBackupRestoreLocation,
    options?: SqlResourcesRetrieveContinuousBackupInformationOptionalParams,
  ): Promise<SqlResourcesRetrieveContinuousBackupInformationResponse>;
}
